import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { ClientApiProps, ICallsignReconfirmationRequestEx, isContestCallsign, isSpecialEvent, isSpecialEventCallSign, isVK0ORVK9, yupFileTestOptions } from '../../common';
import {
  Button, Modal, Label, useValidation, ErrorScope, Checkbox, Select, SelectOption, TextInput, SecurePay,
  SecurePayMethods,
  TokenisedCard,
  Spinner,
  AlertProvider,
  TextArea,
} from 'acma-spa';

import {
  Acma_amateurradiocallsignreconfirmation_statuscode,
  Acma_amateurradiocallsignprefixes,
  Acma_amateurradiocallsigntypes,
  Acma_amateurradiolocations,
  IOperatorCallsign,
  IOperatorPayload,
  getApiClient,
  CallSignReconfirmationRequest,
} from '../../api';

import { FileParameter, IChargingRule, IReassignChargingRule, SpecialEventType } from '../../api/client';
import { Link } from '../../common/FormHelpers';
import DatePicker from 'react-multi-date-picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';

export const schema: yup.ObjectSchema<ICallsignReconfirmationRequestEx> = yup.object({
  id: yup.string(),
  callSignId: yup.string(),
  referenceNumber: yup.string(),
  callsignName: yup.string(),
  prefix: yup.mixed<Acma_amateurradiocallsignprefixes>().oneOf(Object.values(Acma_amateurradiocallsignprefixes)).label('Prefix'),
  type: yup.mixed<Acma_amateurradiocallsigntypes>().oneOf(Object.values(Acma_amateurradiocallsigntypes)).label('Type'),
  location: yup.mixed<Acma_amateurradiolocations>().oneOf(Object.values(Acma_amateurradiolocations)),
  status: yup.mixed<Acma_amateurradiocallsignreconfirmation_statuscode>().oneOf(Object.values(Acma_amateurradiocallsignreconfirmation_statuscode)).optional(),
  fileNames: yup.array().when(['prefix', 'type', 'location'], {
    is: (prefix: Acma_amateurradiocallsignprefixes, type: Acma_amateurradiocallsigntypes, location: Acma_amateurradiolocations) => {
      return isVK0ORVK9(prefix, type, location)
    },
    then: (schema) => schema.defined('At least one document is required').min(1, 'At least one document is required'),
  }).test(yupFileTestOptions),
  confirmation: yup.boolean()
    .when(['prefix', 'type', 'location'], {
      is: (prefix: Acma_amateurradiocallsignprefixes, type: Acma_amateurradiocallsigntypes, location: Acma_amateurradiolocations) =>
        isSpecialEventCallSign(prefix, type, location),
      then: (schema) => schema.required('Confirmation is required.').oneOf([true], 'Confirmation is required.'),
    }),
  contestConfirmation: yup.boolean()
    .when(['prefix', 'type', 'location'], {
      is: (prefix: Acma_amateurradiocallsignprefixes, type: Acma_amateurradiocallsigntypes, location: Acma_amateurradiolocations) =>
        isContestCallsign(prefix, type, location),
      then: (schema) => schema.required('Confirmation is required.').oneOf([true], 'Confirmation is required.'),
    }),
  nextReconfirmDate: yup.date(),
  existsPendingReconfirmationRequest: yup.boolean(),
  tokenisedCard: yup.string(),
  clubName: yup.string(),
  additionalInformation: yup.string().when(['prefix'], {
    is: (prefix: Acma_amateurradiocallsignprefixes) => prefix === Acma_amateurradiocallsignprefixes.SpecialEvent,
    then: (schema) => schema.required('Additional information is required.'),
  }),
  dates: yup
    .string()
    .label('Dates')
    .when(['prefix'], {
      is: (prefix: Acma_amateurradiocallsignprefixes) => prefix == Acma_amateurradiocallsignprefixes.SpecialEvent,
      then: (schema) => schema.required(),
    }),
});

export interface CallsignReconfirmationRequestFormProps extends ClientApiProps {
  reconfirmCallsignTarget: IOperatorCallsign | undefined;
  updateCallsignDetails: (existsPending: boolean | undefined, reconfirmationDate: Date | undefined) => any;
  closeReconfirmDlg: () => any;
  onAuthorisation: () => any;
  onChange: (mode: Partial<IOperatorPayload>) => any;
  onSpinner: (text: string) => any;
}

export const CallsignReconfirmationRequestForm: React.FC<CallsignReconfirmationRequestFormProps> = (props) => {

  const securePayRef = useRef<SecurePayMethods>(null);
  const [loadingText, setLoadingText] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [model, setModel] = useState<ICallsignReconfirmationRequestEx>({});

  const [tokenisedCard, setTokenisedCard] = useState<TokenisedCard | null>(null);
  const [dates, setDates] = useState<string[]>(() => model.dates?.split(',').map((r) => r.trim()) ?? []);

  const [shouldDisplayConfirmationDlg, setShouldDisplayConfirmationDlg] = useState(false);
  const [files, setFiles] = useState<Record<string, File | null>>({});

  const { reconfirmCallsignTarget, updateCallsignDetails, closeReconfirmDlg } = props;
  const [, errors, validate] = useValidation<ICallsignReconfirmationRequestEx>(schema, model, (model) => setModel(model));

  const closConfirmationDilg = () => setShouldDisplayConfirmationDlg(false);

  const [reassignChargingRules, setReassignChargingRules] = useState<IReassignChargingRule[]>([]);

  const [acceptPaymentDetails, setAcceptPaymentDetails] = useState(false);

  useEffect(() => {
    async function load() {
      if (reconfirmCallsignTarget) {
        setErrorText(null);
        setLoadingText("Loading...");

        try {
          const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
          const rules = await client.getReassignChargingRules();
          setReassignChargingRules(rules);

          const model = {
            prefix: reconfirmCallsignTarget.prefix,
            type: reconfirmCallsignTarget.type,
            location: reconfirmCallsignTarget.location,
            callsignName: reconfirmCallsignTarget.name,
            callSignId: reconfirmCallsignTarget.callsignId,
            nextReconfirmDate: reconfirmCallsignTarget.nextReconfirmDate,
            existsPendingReconfirmationRequest: reconfirmCallsignTarget.existsPendingReconfirmationRequest,
            clubName: reconfirmCallsignTarget.clubName
          };

          (
            isVK0ORVK9(reconfirmCallsignTarget?.prefix, reconfirmCallsignTarget?.type, reconfirmCallsignTarget?.location)
            || isSpecialEvent(reconfirmCallsignTarget?.prefix)
            || isContestCallsign(reconfirmCallsignTarget?.prefix, reconfirmCallsignTarget?.type, reconfirmCallsignTarget?.location)
          )
            && setAcceptPaymentDetails(true);

          setModel(model);
        } catch (e: any) {
          setErrorText(e.message ? e.message : "Unexpected error");
        }
        finally {
          setLoadingText(null);
        }
      }
    }

    load();
  }, [reconfirmCallsignTarget]);

  useEffect(() => {

    console.log('tokenisedCard useState');

    async function callReconfirmCallsign() {
      if (!tokenisedCard) {
        return;
      }

      console.log('now calling reconfirmCallsign');

      await reconfirmCallsign();
    };

    callReconfirmCallsign();
  }, [tokenisedCard]);


  const getPrice = (model: ICallsignReconfirmationRequestEx): any => {
    if (isVK0ORVK9(model.prefix, model.type, model.location))
      return reassignChargingRules.find(element => element.specialEventType == SpecialEventType.VK09Callsign)?.price;

    if (isSpecialEvent(model.prefix))
      return reassignChargingRules.find(element => element.specialEventType == SpecialEventType.SpecialCallsign)?.price;

    if (isContestCallsign(model.prefix, model.type, model.location))
      return reassignChargingRules.find(element => element.specialEventType == SpecialEventType.ContestCallsign)?.price;
  }

  const onFileAdded = (arr: File[]) => {
    const newFiles = { ...files };
    for (const f of arr) {
      const index = f.name.lastIndexOf('.');
      let filename = f.name;
      let extension = '';

      if (index >= 0) {
        filename = f.name.substring(0, index);
        extension = f.name.substring(index);
      }

      // This looks like it is trying to find unique filenames.
      for (let j = 0; ; j++) {
        let key = f.name;
        if (j > 0) {
          key = `${filename} (${j})${extension}`;
        }
        if (!(key in newFiles)) {
          newFiles[key] = f;
          break;
        }
      }
    }

    setFiles(newFiles);
    return Object.keys(newFiles);
  };

  const onFileRemoved = (name: string) => {
    const newFiles = { ...files };
    if (name in newFiles) delete newFiles[name];
    setFiles(newFiles);
    return Object.keys(newFiles);
  };

  const HeaderInfo = () => <>
    <div className="mb-3">
      Please complete this form and supply supporting documents (where applicable) if you would like to apply for reassignment of your call sign. Your application will be processed in accordance with the {Link(props.contentUrls.examRules, 'Radiocommunications (Amateur Stations) Class Licence 2023')} and the {Link(props.contentUrls.callSignPolicy, 'ACMA call sign policy')}. Once you submit this form you will receive an email from the ACMA confirming that your application has been received.
    </div>
  </>

  const PrivacyPolicy = () => <>
    <div className="alert alert-info" role="alert">
      The personal information being collected in this form is to enable the ACMA to decide whether it will reassign your call sign to you consistent with the {Link(props.contentUrls.callSignPolicy, 'ACMA Call sign policy')}. The information will also be used by the ACMA to administer call signs and facilitate call sign applications and assignments.
      <br />
      <br />
      The ACMA publishes a call sign register on its website that lists all call signs and, if the call sign is allocated, the respective qualification level associated with the allocated call sign. The call sign register does not contain any other information about a licence holder.
      <br />
      <br />
      The ACMA will not use any of the personal information you provide in this form for any other purpose, nor will we disclose it, unless we have your consent or we are otherwise permitted to do so under the <em>Privacy Act 1988</em>.
      <br />
      <br />
      Further information about the {Link(props.contentUrls.privacyPolicy, "Privacy Act and the ACMA's Privacy Policy")} is on our website. The Privacy Policy contains details about how you may access personal information about you that is held by the ACMA, and seek the correction of such information. It also explains how you may complain about a breach of the {Link(props.contentUrls.privacyAct, 'Privacy Act')} and how we will deal with such a complaint.
    </div>
  </>

  const ContestView =
    <>
      <HeaderInfo />
      <div className="row">
        <div className="col-sm-8 mb-3">
          <Label id="location" label="Location where you will use call sign" error={errors?.location} required={false}>
            <TextInput readonly={true} value={model.location?.toString() == "AustralianExternalTerritories" ? "Australian External Territories" : model.location?.toString() || ''} onChange={() => { }} />
          </Label>
        </div>
      </div>

      {model.clubName && (
        <div className="row">
          <div className="col-sm-8 mb-3">
            <Label id="clubName" label="Name of the club you are applying on behalf of">
              <TextInput readonly={true} value={model.clubName || ''} maxLength={400} onChange={() => { }} />
            </Label>
          </div>
        </div>
      )}

      <h5 className="my-2">Payment details</h5>
      <h6>Total: ${getPrice(model)} AUD</h6>
      <SecurePay
        clientId={props.securePayClientId ?? ''}
        merchantCode={props.securePayMerchantCode ?? ''}
        uri={`${props.securePaySrc}`}
        onLoadComplete={() => { }}
        onTokeniseError={() => { console.log('error'); setLoadingText(null); }}
        onTokeniseSuccess={(v) => {
          setLoadingText(null);
          setTokenisedCard(v);
        }}
        ref={securePayRef}
      />

      <div className="row">
        <div className="col mb-3">
          <ErrorScope id="confirmation" error={errors?.confirmation}>
            <Checkbox
              id="confirmation"
              label="I confirm that the information provided is true and correct."
              checked={model.confirmation ?? false}
              onChange={(v) => setModel({ ...model, confirmation: v })}
              required
            />
          </ErrorScope>
        </div>
      </div>

      <div className="row">
        <div className="col mb-3">
          <ErrorScope id="contestConfirmation" error={errors?.contestConfirmation}>
            <Checkbox
              id="contestConfirmation"
              label="I understand that I am applying for reassignment of a contest call sign and that, if my application is approved, I can only use this call sign during amateur radio contests"
              checked={model.contestConfirmation ?? false}
              onChange={(v) => setModel({ ...model, contestConfirmation: v })}
              required
            />
          </ErrorScope>
        </div>
      </div>

      <PrivacyPolicy />

    </>

  const SpecialEventView =
    <>
      <HeaderInfo />
      <div className="row">
        <div className="col-sm-12 mb-3">
          <Label
            id="dates"
            label={`Date/s of local, national or international day of significance you plan to use this call sign.`}
            required
            error={errors?.dates}>
            <DatePicker
              multiple
              inputClass="amateurradio__au-text-input amateurradio__au-text-input--block mt-2"
              containerStyle={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={dates}
              plugins={[<DatePanel key={1} />]}
              onChange={(v) => {
                if (Array.isArray(v)) {
                  const dateStrings: string[] = v.map((r) => r.toString());
                  const dates = dateStrings.join(', ');
                  setModel({ ...model, dates: dates });
                  setDates(dateStrings);
                }
              }}
            />
          </Label>
        </div>
      </div>


      <div className="row">
        <div className="col-sm-12 mb-3">
          <Label
            id="additionalInformation"
            label={
              <>
                If you want to continue with the application, please outline the event that you are applying to use this special event call sign for in
                the box below. Please also indicate how the proposed special event call sign relates to the date of national/international
                significance. This application will be assessed against the ACMA call sign policy.
              </>
            }
            error={errors?.additionalInformation}
            required>
            <TextArea
              value={model.additionalInformation || ''}
              onChange={(v) => setModel({ ...model, additionalInformation: v })}
              maxLength={400}
              rows={5}
            />
          </Label>
        </div>
      </div>

      {model.clubName && (
        <div className="row">
          <div className="col-sm-8 mb-3">
            <Label id="clubName" label="Name of the club you are applying on behalf of">
              <TextInput readonly={true} value={model.clubName || ''} maxLength={400} onChange={() => { }} />
            </Label>
          </div>
        </div>
      )}

      {
        <>
          <h5 className="my-2">Payment details</h5>
          <h6>Total: ${getPrice(model)} AUD</h6>
          <SecurePay
            clientId={props.securePayClientId ?? ''}
            merchantCode={props.securePayMerchantCode ?? ''}
            uri={`${props.securePaySrc}`}
            onLoadComplete={() => { }}
            onTokeniseError={() => { console.log('error'); setLoadingText(null); }}
            onTokeniseSuccess={(v) => {
              setLoadingText(null);
              setTokenisedCard(v);
            }}
            ref={securePayRef}
          />

          {
            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation" error={errors?.confirmation}>
                  <Checkbox
                    id="confirmation"
                    label="I confirm that the information provided is true and correct."
                    checked={model.confirmation ?? false}
                    onChange={(v) => setModel({ ...model, confirmation: v })}
                    required
                  />
                </ErrorScope>
              </div>
            </div>
          }
        </>
      }

      <PrivacyPolicy />
    </>

  const VK0VK9View =
    <>
      <HeaderInfo />

      <div className="row">
        <div className="col-sm-8 mb-3">
          <Label id="location" label="Location where you will use call sign" error={errors?.location} required={false}>
            <TextInput readonly={true} value={model.location?.toString() == "AustralianExternalTerritories" ? "Australian External Territories" : model.location?.toString() || ''} onChange={() => { }} />
          </Label>
        </div>
      </div>

      {model.clubName && (
        <div className="row">
          <div className="col-sm-8 mb-3">
            <Label id="clubName" label="Name of the club you are applying on behalf of">
              <TextInput readonly={true} value={model.clubName || ''} maxLength={400} onChange={() => { }} />
            </Label>
          </div>
        </div>
      )}

      <>
        <h5 className="mb-2">
          Supporting documents
          <span style={{ color: 'red', display: 'inline' }} aria-hidden="true">
            *
          </span>
        </h5>
        <p>
          Please provide evidence of your residency or your intention to travel to the{' '}
          {model.location === Acma_amateurradiolocations.Antarctic ? 'Antarctic' : 'Australian external territory'}. For further information about
          what evidence would be acceptable for this purpose please refer to the ACMA's {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
        </p>

        <ErrorScope id="fileNames" error={errors.fileNames}>
          {model.fileNames && model.fileNames.length > 0 && (
            <Label id="files" label="Uploaded Files">
              <ul className="list-unstyled mt-2">
                {model.fileNames.map((r, i) => (
                  <li key={i}>
                    {r}{' '}
                    <a
                      href="#"
                      title="Remove"
                      onClick={(e) => {
                        e.preventDefault();
                        const fileNames = onFileRemoved(r);
                        setModel({ ...model, fileNames });
                      }}>
                      X
                    </a>
                  </li>
                ))}
              </ul>
            </Label>
          )}

          {(
            <Button
              id="fileNames"
              label="Upload document"
              className="mb-3"
              onClick={() => document.getElementById('callsign-document-upload')?.click()}
            />
          )}
        </ErrorScope>

        <input
          type="file"
          id="callsign-document-upload"
          style={{ display: 'none' }}
          multiple={true}
          accept={'.docx,.pdf,.xlsx,.png,.jpeg,.jpg,.gif'}
          onChange={(e) => {
            const fileNames = onFileAdded(e.target.files ? Array.from(e.target.files) : []);
            setModel({ ...model, fileNames });
            e.target.value = '';
          }}
        />

      </>

      {
        <>
          <h5 className="my-2">Payment details</h5>
          <h6>Total: ${getPrice(model)} AUD</h6>
          <SecurePay
            clientId={props.securePayClientId ?? ''}
            merchantCode={props.securePayMerchantCode ?? ''}
            uri={`${props.securePaySrc}`}
            onLoadComplete={() => { }}
            onTokeniseError={() => { console.log('error'); setLoadingText(null); }}
            onTokeniseSuccess={(v) => {
              setLoadingText(null);
              setTokenisedCard(v);
            }}
            ref={securePayRef}
          />

          {
            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation" error={errors?.confirmation}>
                  <Checkbox
                    id="confirmation"
                    label="I confirm that the information provided is true and correct."
                    checked={model.confirmation ?? false}
                    onChange={(v) => setModel({ ...model, confirmation: v })}
                    required
                  />
                </ErrorScope>
              </div>
            </div>
          }

        </>
      }

      <PrivacyPolicy />
    </>

  const reconfirmCallsign = async () => {

    console.log('reconfirmCallsign called');

    const result = await validate('confirmCallsignDlg');

    console.log(result);
    console.log(errors);

    if (result) {
      closeReconfirmDlg();
      const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);

      try {
        props.onSpinner('Submitting reconfirmation request...');

        model.tokenisedCard = tokenisedCard?.token;
        console.log(model);
        const status = isSpecialEventCallSign(model.prefix, model.type, model.location)
          ? Acma_amateurradiocallsignreconfirmation_statuscode.Submitted
          : Acma_amateurradiocallsignreconfirmation_statuscode.Approved;

        console.log(status);

        const request = new CallSignReconfirmationRequest({ ...model, status: status });

        console.log(request);

        const fileData = [];
        const fileNames = [];
        for (const file in files) {
          fileData.push({ data: files[file], fileName: file } as FileParameter);
          fileNames.push(file);
        }

        const result = await client.callsignApplicationReconfirmation(JSON.stringify(request), JSON.stringify(fileNames), fileData);
        if (result) {
          if (isSpecialEventCallSign(model.prefix, model.type, model.location)) {
            updateCallsignDetails(true, undefined);
            setShouldDisplayConfirmationDlg(true);
          } else {
            updateCallsignDetails(undefined, model.nextReconfirmDate);
          }
        }
      } finally {
        props.onSpinner('');
      }
    }
  };

  return (
    <>
      <Modal size='xl'
        id="confirmCallsignDlg"
        title={isSpecialEventCallSign(model.prefix, model.type, model.location) ? "Callsign reassignment" : "Callsign reconfirm"}
        open={!!reconfirmCallsignTarget}
        onClose={closeReconfirmDlg}
        rightButtons={[
          {
            label: isSpecialEventCallSign(model.prefix, model.type, model.location) ? "Apply for reassignment" : "Reconfirm",
            onClick: async () => {
              if (acceptPaymentDetails)
                securePayRef.current?.tokenise();
              else
                await reconfirmCallsign();
            },
          },
          { label: 'Cancel', mode: 'secondary', onClick: closeReconfirmDlg },
        ]}>

        {
          !isSpecialEventCallSign(reconfirmCallsignTarget?.prefix, reconfirmCallsignTarget?.type, reconfirmCallsignTarget?.location) &&
          <>
            <p>
              You have indicated that you are continuing to use your call sign. You will need to reconfirm your call sign before{' '}
              {model?.nextReconfirmDate?.toLocaleDateString()}
            </p>
          </>
        }

        <Spinner isLoading={loadingText ? true : false} loadingText={loadingText || undefined}>
          {errorText ? <AlertProvider messages={[errorText]} dismiss={true} color="danger" /> : <></>}
          <div id="form-container">
            {isVK0ORVK9(reconfirmCallsignTarget?.prefix, reconfirmCallsignTarget?.type, reconfirmCallsignTarget?.location) && (VK0VK9View)}

            {isSpecialEvent(reconfirmCallsignTarget?.prefix) && (SpecialEventView)}

            {isContestCallsign(reconfirmCallsignTarget?.prefix, reconfirmCallsignTarget?.type, reconfirmCallsignTarget?.location) && (ContestView)}
          </div>
        </Spinner>
      </Modal>

      <Modal
        id="submittionConfirmationDlg"
        title={isSpecialEventCallSign(model.prefix, model.type, model.location) ? "Reassignment request" : "Reconfirm request"}
        open={!!shouldDisplayConfirmationDlg}
        onClose={closConfirmationDilg}
        rightButtons={[
          {
            label: 'Ok',
            onClick: async () => {
              await closConfirmationDilg();
            },
          },
        ]}>
        <p>
          Thank you for submitting your request for reassignment of your call sign. We will contact you directly if we have any questions.
        </p>
      </Modal>
    </>
  );
};
